/* import react and the stuff we need */
import React, {Component} from 'react';
/* import the 3rd party libraries we are using */
import {Tab, TabList, TabPanel, Tabs} from 'react-tabs';
import {Button, Modal, ModalBody, ModalHeader} from "reactstrap";
/* import the 3rd party css files we are using */
import "react-tabs/style/react-tabs.css";
import './css/bootstrap.min.css';

import './App.css';

import Navigator from "./components/navigator";
import PeripheralPinArray from './components/PeripheralPinArray';
import FilterablePeripheralTable from './components/filterablePeripheralList';
import * as deviceConstants from './devices';
import {deepCopy, get_version, x2xxPlats, m33Plats} from './utes/appUtils';
import SvgComponent from "./components/boardVisual";
import {pin_state} from "./components/PeripheralPinArray/device/peripheral";

// MACROS
const DEFAULT_BOARD = 'ATM3330x-xxxxQN';
const TOTAL_PINS = 52;

function is_stacked_flash(board_str) {
    if (x2xxPlats.includes(get_version(board_str))) {
        return (board_str.slice(-1) === '2');
    } else if (m33Plats.includes(get_version(board_str))) {
        // check for -x[LWY]xxxx
        let size = board_str.split('-')[1][1]
        return (size === 'L' || size === 'W' || size === 'Y');
    } else {
        return false;
    }
}


class App extends Component {

    constructor(props) {
        super(props);
        let selected_board = DEFAULT_BOARD;
        let version = get_version(selected_board);
        let total_pins = TOTAL_PINS;
        let currentPeripherals = [...deviceConstants.peripheral_map[version]];
        let currentAvailablePins = deviceConstants.boardPinMap[selected_board];
        /* QSPI is only required for stacked flash boards */
        let QSPIrequired = is_stacked_flash(selected_board);

        /**
         *  First, we need to add some parameters that dont need to be saved in the Const file
         *  Mainly, which pins are selected for each Peripheral
         *  and if the peripheral is active or not.
         **/
        let pinStatus = Array(total_pins).fill(null);
        currentPeripherals.forEach(function (peri) {
            App.initPeripheral(peri, total_pins, QSPIrequired);
            /* set SPI2 pins because they are required*/
            if (peri.name === 'qspi') {
                App.initQSPI(peri, pinStatus, selected_board, QSPIrequired);
            }
        });

        this.state = {
            height: 0,
            width: 0,
            /* these are the more application specific state variables*/
            total_pins: total_pins,
            TMCActive: false,
            QSPIrequired: QSPIrequired,
            selectedBoard: selected_board,
            peripherals: currentPeripherals,
            pinMap: currentAvailablePins,
            pullUps: Array(total_pins).fill(-1),
            pinStatus: pinStatus,
            updateAnnotate:false,
            showLegend: false,
        };


        /* set up some binding to make everything work */
        this.handleTMCChange = this.handleTMCChange.bind(this);
        this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
        this.handleBoardChange = this.handleBoardChange.bind(this);
        this.clickPinCallback = this.clickPinCallback.bind(this);
        this.clickedPullupCallback = this.clickedPullupCallback.bind(this);
        this.updatePositionOfAnnotate = this.updatePositionOfAnnotate.bind(this);
        this.toggleLegendModal = this.toggleLegendModal.bind(this);
    }

    componentDidMount() {
        this.updateWindowDimensions();
        window.addEventListener('resize', this.updateWindowDimensions);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions);
    }

    /**
     * called when the window changes size, this is used for the page splitter
     */
    updateWindowDimensions() {
        this.setState({width: window.innerWidth, height: window.innerHeight});
    }

    updatePositionOfAnnotate() {
        this.setState({updateAnnotate: true});
    }

    /************************************************ STATIC FUNCTIONS ************************************************/

    /** Checks if the given pinArray is empty
     *
     * @param pinArray - array of pin states
     * @returns {boolean} true if empty, false otherwise
     */
    static isEmptyConfig(pinArray) {
        let ret = pinArray.some((pin)=>{
            return (pin !== null) && (pin !== -1);
        });
        return !ret;
    }

    /** Initializes a peripheral to base state.
     *
     * @param peri - object of peripheral
     * @param num_pins - number of pins for current layout
     * @param reqQSPI - boolean, QSPI required.
     */
    static initPeripheral(peri, num_pins, reqQSPI) {
        peri.pinSelection = Array(peri.numPins).fill(null);
        if (peri.selection_type ==="multi") {
            peri.pinIO = Array(num_pins).fill(0);
            peri.userPinNames = Array(num_pins).fill("");
            peri.pinSelection = Array(peri.numPins);
            for (let i = 0; i < peri.numPins; i++) {
                peri.pinSelection[i] = [];
            }
        }
        if (peri.name === "ksi" || peri.name === "kso") {
            peri.rowCol = new Array(peri.numPins).fill(-1);
        } else if (peri.name === "pwm") {
            peri.chanInst = Array(peri.numPins).fill("");
        }
        peri.selected = ((peri.name === 'qspi') && (reqQSPI));

    }

    /** Initializes the QSPI peripheral.
     *
     * @param peripheral
     * @param pinStatus
     * @param selected_board
     * @param reqQSPI
     */
    static initQSPI(peripheral, pinStatus, selected_board, reqQSPI) {
        if ((reqQSPI)) {
            let QSPI_inUse = deviceConstants.boardQSPImap[selected_board].inUse;
            let selection = deviceConstants.boardQSPImap[selected_board].pinLocation.flat();
            for (let i = 0; i < peripheral.numPins; i++) {
                if (QSPI_inUse[i] === 1) {
                    let pin = selection[i];
                    peripheral.pinSelection[i] = pin;
                    pinStatus[pin] = {
                        periName: 'qspi',
                        pos: i
                    };
                }
            }
            peripheral.required = deviceConstants.boardQSPImap[selected_board].inUse;
            peripheral.pinLocation = deviceConstants.boardQSPImap[selected_board].pinLocation;
        } else if ((!reqQSPI) && x2xxPlats.includes(get_version(selected_board))) {
            // x2xx Parts have QSPI limitation via ROM
            peripheral.required = deviceConstants.boardQSPImap[selected_board].inUse;
            peripheral.pinLocation = deviceConstants.boardQSPImap[selected_board].pinLocation;
        } else if (selected_board.match(/ATM3405-5PxxBV/) !== null) {
            // No QSPI on ATM3405-5PxxBV
            peripheral.pinLocation = [[], [], [], [], [], []];
        }
    }

    /******************************************************************************************************************/

    /**
     * Update the state of the application to include the selected peripheral from the list.
     * @param clickedPeripheral
     */
    clickListCallback = (clickedPeripheral) => {
        this.setState((prevState) => {
            /* this isn't the prettiest of code... but objs are passed by reference so it works */
            let peri = prevState.peripherals.find(function (element) {
                return element.name === clickedPeripheral.name;
            });
            let newPeripheralSelected = !peri.selected;
            peri.selected = newPeripheralSelected;
            /* deselecting an active peripheral*/
            let selectedBoard = prevState.selectedBoard;
            // x2xx Parts have certain QSPI pin requirements due to ROM functionality
            if (peri.name === 'qspi' && x2xxPlats.includes(get_version(selectedBoard))) {
                if (!newPeripheralSelected && !is_stacked_flash(selectedBoard)) {
                    /* taking it off the active list*/
                    peri.pinSelection.forEach((pinLocation) => {
                        if (pinLocation !== null) {
                            prevState.pinStatus[pinLocation] = null;
                        }
                    });
                    peri.pinSelection = Array(peri.numPins).fill(null);
                } else {
                    let QSPI_inUse = deviceConstants.boardQSPImap[selectedBoard].inUse;
                    let selection = deviceConstants.boardQSPImap[selectedBoard].pinLocation.flat();
                    for (let i = 0; i < peri.numPins; i++) {
                        if (QSPI_inUse[i] === 1) {
                            let pin = selection[i];
                            peri.pinSelection[i] = pin;
                            prevState.pinStatus[pin] = {
                                periName: 'qspi',
                                pos: i
                            };
                        }
                    }
                    peri.pinLocation = deviceConstants.boardQSPImap[selectedBoard].pinLocation;
                }
            }

            return {
                peripherals: prevState.peripherals,
                pinStatus: prevState.pinStatus
            }
        });
    };

    /**
     * Update the state of the application to set pull-ups on the clicked pin.
     * @param cP
     */
    clickedPullupCallback = (cP) =>{
        let clickedPin = this.state.pinMap[cP];
        this.setState((state)=>{
            let pullups = state.pullUps;
            pullups[clickedPin] = -pullups[clickedPin];
            return {pullUps: pullups};
        })
    };

    /**
     * manages the state of the pins when a box is clicked in the matrix
     * @param peripheral_name name of the peripheral that pin was selected for
     * @param X index of the selected signal for the given peripheral (ex: MOSI, MISO)
     * @param cP the physical pin selected (ex: P0, P1)
     */
    clickPinCallback = (peripheral_name, X, cP) => {
        /* due to there not being a 1-to-1 mapping from pin number to box, we
         * need to get the physical pin */
        let clicked_pin = this.state.pinMap[cP];
        let all_pin_status = [...this.state.pinStatus];
        let peripherals = [...this.state.peripherals];
        let pullUps = [...this.state.pullUps];

        console.assert(
            !((this.state.TMCActive) && (clicked_pin < 3)),
            "Pins  0-2 are not selectable when TMC is active"
        );

        /* TODO: pass the peripheral so we do not have to do this search */
        let active_peripheral = peripherals.find(function (element) {
            return element.name === peripheral_name;
        });

        // Find if the selected signal already has an assignment
        let current_assignment = this.state.pinStatus.findIndex(function (element) {
            return element !== null && element.periName === peripheral_name && element.pos === X;
        });

        let current_pin_state = all_pin_status[clicked_pin];
        if (!active_peripheral.required[X]) {
            switch(active_peripheral.name) {
                case "gpio": {
                    const inUsePeri = (current_pin_state === null) ? null :
                        peripherals.find(function (element) {
                            return element.name === current_pin_state.periName;
                        });
                    const inUse = (inUsePeri) && (inUsePeri.name !== 'gpio');
                    // short circuit if trying to override a required pin
                    if ((inUse) && (inUsePeri.required[X])) {
                        break;
                    }
                    // abort if user requested
                    if (inUse &&
                        !(window.confirm(`Pin ${clicked_pin} is currently set to [${inUsePeri.name}]` +
                            `[${inUsePeri.pinNames[current_pin_state.pos]}]\n Are you sure you want to overwrite?`))) {
                        break;
                    }
                    if (inUse) {
                        inUsePeri.pinLocation[current_pin_state.pos].forEach((pin) => {
                            all_pin_status[pin] = null;
                            pullUps[pin] = pin_state.PULL_UP_RESET;
                        });
                        inUsePeri.pinSelection[current_pin_state.pos] = null;
                    }
                    active_peripheral.pinIO[clicked_pin] = (active_peripheral.pinIO[clicked_pin] + 1) % 3;
                    let pin_gpio_state = active_peripheral.pinIO[clicked_pin];
                    all_pin_status[clicked_pin] = (pin_gpio_state === 0) ?
                        null : {periName: active_peripheral.name, pos: X};
                } break;
                case "gadc": {
                    let inUsePeris = [];
                    let overwrite = active_peripheral.pinLocation[X].every((pin) => {
                        const ps = all_pin_status[pin];
                        if (ps) {
                            let peri = peripherals.find(function (element) {
                                return element.name === ps.periName;
                            });
                            inUsePeris.push([pin, ps.pos,peri]);
                            return (peri.name === active_peripheral.name) ||
                                (window.confirm(`Pin ${pin} is currently set to [${peri.name}]` +
                                `[${peri.pinNames[ps.pos]}]\n Are you sure you want to overwrite?`));
                        }
                        return true;
                    });

                    if (!overwrite) {
                        break;
                    }
                    inUsePeris.forEach((periInfo) => {
                        let inUsePin = periInfo[0];
                        let inUsePos = periInfo[1];
                        let inUsePeri = periInfo[2];
                        if (inUsePeri.name === 'gpio') {
                            inUsePeri.pinIO[inUsePin] = -1;
                        } else if (inUsePeri.name === 'gadc') {
                            active_peripheral.pinLocation[inUsePos].forEach((pin) => {
                                all_pin_status[pin] =  null;
                                pullUps[pin] = pin_state.PULL_UP_RESET;
                            });
                        } else {
                            all_pin_status[inUsePin] = null;
                            pullUps[inUsePin] = pin_state.PULL_UP_RESET;
                            inUsePeri.pinSelection[inUsePos] = null;
                        }
                    });

                    if ((active_peripheral.pinSelection[X] !== null) && (active_peripheral.pinSelection[X] !== -1)) {
                        all_pin_status[active_peripheral.pinSelection[X]] =  null;
                        pullUps[active_peripheral.pinSelection[X]] = pin_state.PULL_UP_RESET;
                        active_peripheral.pinSelection[X] = null;
                    } else {
                        if (active_peripheral.pinNames[X].includes("Diff")) {
                            active_peripheral.pinSelection[X] = active_peripheral.pinLocation[X];
                            for (var i = 0; i <  active_peripheral.pinSelection.length; i++) {
                                if (X !== i) {
                                    for (var j = 0; j < active_peripheral.pinSelection[X].length; j++) {
                                        if (active_peripheral.pinSelection[i] === active_peripheral.pinSelection[X][j])
                                            active_peripheral.pinSelection[i] = null;
                                    }
                                }
                            }
                        } else {
                            active_peripheral.pinSelection[X] = clicked_pin;
                            for (i = 0; i < active_peripheral.pinSelection.length; i++) {
                                if (Array.isArray(active_peripheral.pinSelection[i])) {
                                    if (active_peripheral.pinSelection[i].includes(clicked_pin))  {
                                        active_peripheral.pinSelection[i] = null;
                                    }
                                }
                            }
                        }
                    }

                    let clear_pins = (current_pin_state !== null) &&
                        ((current_pin_state.periName === peripheral_name) && (current_pin_state.pos === X));
                    active_peripheral.pinLocation[X].forEach((pin) => {
                        all_pin_status[pin] = (clear_pins) ? null :
                            {
                                periName: active_peripheral.name,
                                pos: X
                            };
                        pullUps[pin] = (clear_pins) ?
                            pin_state.PULL_UP_RESET : pin_state.DISABLED;
                    });
                } break;

                default: {
                    const inUsePeri = (current_pin_state === null) ? null :
                        peripherals.find(function (element) {
                            return element.name === current_pin_state.periName;
                        });
                    const inUse = (inUsePeri) && (inUsePeri.name !== active_peripheral.name);
                    // short circuit if trying to override a required pin
                    if ((inUse) && (inUsePeri.required[X])) {
                        break;
                    }
                    // abort if user requested
                    if (inUse &&
                        !(window.confirm(`Pin ${clicked_pin} is currently set to [${inUsePeri.name}]` +
                            `[${inUsePeri.pinNames[current_pin_state.pos]}]\n Are you sure you want to overwrite?`))) {
                        break;
                    }
                    if (inUse) {
                        inUsePeri.pinLocation[current_pin_state.pos].forEach((pin) => {
                            pullUps[pin] = pin_state.PULL_UP_RESET;
                        });
                        if (inUsePeri.name === "gpio") {
                            inUsePeri.pinIO[clicked_pin] = 0;
                            inUsePeri.userPinNames[clicked_pin] = "";
                        } else {
                            if (["ksi", "kso"].includes(inUsePeri.name)) {
                                inUsePeri.rowCol[current_pin_state.pos] = -1;
                            } else if (inUsePeri.name === "pwm") {
                                inUsePeri.chanInst[current_pin_state.pos] = "";
                            }
                            inUsePeri.pinSelection[current_pin_state.pos] = null;
                        }
                    }
                    // Reset pin if clicking already selected value
                    if (active_peripheral.pinSelection[X] === clicked_pin) {
                        all_pin_status[active_peripheral.pinSelection[X]] = null;
                        pullUps[active_peripheral.pinSelection[X]] = pin_state.PULL_UP_RESET;
                        active_peripheral.pinSelection[X] = null;

                        if (["ksi", "kso"].includes(active_peripheral.name)) {
                            active_peripheral.rowCol[current_pin_state.pos] = -1;
                        } else if (active_peripheral.name === "pwm") {
                            active_peripheral.chanInst[current_pin_state.pos] = "";
                        }
                    } else {
                        // Switch to newly selected pin if peripheral already in use
                        active_peripheral.pinSelection[X] = clicked_pin;
                        if (current_assignment !== -1) {
                            // Clear the previous assignment for the signal
                            all_pin_status[current_assignment] = null;
                            pullUps[current_assignment] = pin_state.PULL_UP_RESET
                        }
                    }

                    let clear_pins = (current_pin_state !== null) &&
                        ((current_pin_state.periName === peripheral_name) && (current_pin_state.pos === X));
                    if (!clear_pins && active_peripheral.name.includes('shub')) {
                        pullUps[clicked_pin] = pin_state.DISABLED;
                    }
                    if (!clear_pins && active_peripheral.name.includes('xpaon')) {
                        pullUps[clicked_pin] = pin_state.DISABLED;
                    }

                    // Clear existing pin selection if used elsewhere
                    for (i = 0; i < active_peripheral.pinSelection.length; i++) {
                        if (i !== X && active_peripheral.pinSelection[i] === clicked_pin) {
                            active_peripheral.pinSelection[i] = null;
                        }
                    }

                    all_pin_status[clicked_pin] = (clear_pins) ? null :
                        {
                            periName: active_peripheral.name,
                            pos: X
                        };
                    pullUps[clicked_pin] = (clear_pins) ?
                        pin_state.PULL_UP_RESET : pin_state.DISABLED;
                } break;
            }

            this.setState(() => {
                return {peripherals: peripherals,
                    pinStatus: all_pin_status,
                    pullUps: pullUps};
            });
        }
    };

    /**
     * update the state in response to switching from board layouts
     * @param e
     */
    handleBoardChange(e) {
        let selected_board = e.target.value;
        /* update the board to the new board and change the pinMap. */
        if ((App.isEmptyConfig(this.state.pinStatus)) ||
            (window.confirm("This will overwrite the current configuration." +
                " Are you sure you want to continue?"))) {
            let version = get_version(selected_board);
            let total_pins = TOTAL_PINS;
            let pinStatus = Array(total_pins).fill(null);
            let QSPIrequired = is_stacked_flash(selected_board);
            let currentPeripherals = deepCopy(
                deviceConstants.peripheral_map[version]);
            currentPeripherals.forEach(function (peri) {
                App.initPeripheral(peri,total_pins,QSPIrequired);
                if (peri.name === 'qspi') {
                    App.initQSPI(peri, pinStatus, selected_board, QSPIrequired);
                }
            });

            this.setState(() => {
                return ({
                    selectedBoard: selected_board,
                    QSPIrequired: QSPIrequired,
                    peripherals: currentPeripherals,
                    pinMap: deepCopy(
                        deviceConstants.boardPinMap[selected_board].slice()),
                    pullUps: Array(total_pins).fill(-1),
                    pinStatus: pinStatus,
                });

            });
        }
    }
    handleTMCChange() {
        this.setState((prevState) => {
            let newTMCActive = !prevState.TMCActive;
            let ps = prevState.pinStatus;
            let pu = prevState.pullUps;

            if (newTMCActive === true) {
                if (ps[0] !== null) {
                    let inUsePeri = prevState.peripherals.find(function (element) {
                        return element.name === ps[0].periName;});
                    if (inUsePeri.name === 'gpio') {
                        inUsePeri.pinIO[0] = 0;
                    } else {
                        inUsePeri.pinSelection[ps[0].pos] = null;
                    }
                    ps[0] = null;
                    pu[0] = pin_state.PULL_UP_RESET
                }
                if (ps[1] !== null) {
                    let inUsePeri = prevState.peripherals.find(function (element) {
                        return element.name === ps[1].periName;});
                    if (inUsePeri.name === 'gpio') {
                        inUsePeri.pinIO[1] = 0;
                    } else {
                        inUsePeri.pinSelection[ps[1]] = null;
                    }
                    ps[1] = null;
                    pu[1] = pin_state.PULL_UP_RESET
                }
                if (ps[2] !== null) {
                    let inUsePeri = prevState.peripherals.find(function (element) {
                        return element.name === ps[2].periName;});
                    if (inUsePeri.name === 'gpio') {
                        inUsePeri.pinIO[2] = 0;
                    } else {
                        inUsePeri.pinSelection[ps[2]] = null;
                    }
                    ps[2] = null;
                    pu[2] = pin_state.PULL_UP_RESET
                }
            }

            return {
                TMCActive: newTMCActive,
                pinStatus: ps,
                pullUps: pu
            }
        });

    }

    toggleLegendModal(data={}) {
        this.setState(prevState => ({
            showLegend: !prevState.showLegend,
        }));
    }

    render() {
        let tabDivStyle = {
            paddingTop: '6px',
            height: "100%",
            position: "relative",
        };

        let containerStyle = {
            position: "fixed",
            top: '80px', // for navbar
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            overflow: "hidden", // independent scrollbars in children
        };

        let modalButtonStyle = {
            float: "right",
            textDecoration:"underline",
            marginRight: "8px",
            fontWeight: "bold",
            fontSize: "12px",
        };

        let modalListStyle = {
            display: "inline-block",
            verticalAlign: "top",
            width: "150px",
            outline: "2px solid green",
            marginRight: "10px"
        }

        let QSPI = this.state.peripherals.find(function (element) {
            return element.name === 'qspi';
        }) || { selected: false };

        let activeList = this.state.pinStatus.filter((pin, index) => {
            return this.state.pinMap.includes(index)
        }).map((pin, index) => {
            if ((this.state.TMCActive) && (index < 3)) {
                if (index === 0) {
                    return {pin:'P0', Peripheral: 'TMC', signal: 'jtag_sel'}
                } else if (index === 1) {
                    return {pin:'P1', Peripheral: 'TMC', signal: 'swdclk'}
                } else if (index === 2) {
                    return {pin:'P2', Peripheral: 'TMC', signal: 'swdio'}
                }
            } else if (pin !== null) {
                let inUsePeri = this.state.peripherals.find(function (element) {
                    return element.name === pin.periName;
                });
                let pinName = inUsePeri.pinNames[pin.pos];
                let pullUpPin = (this.state.pullUps[index] !== 1) ? "" : "active";

                return {pin:"P"+this.state.pinMap[index], Peripheral: pin.periName, signal: pinName, pullup: pullUpPin}
            }
            return null;
        });

        activeList = activeList.filter(function(e) {return e});
        let is_x2xx_stacked = x2xxPlats.includes(get_version(this.state.selectedBoard)) && is_stacked_flash(this.state.selectedBoard);
        let is_x2xx_csp = this.state.selectedBoard === 'ATMx251';
        return (
            <div className="App">
                <Navigator
                    selectedBoard={this.state.selectedBoard}
                    boardCallback={this.handleBoardChange}
                />
                <div style={containerStyle}>
                    <div style={{ flex: 2, borderRight: '4px solid #ccc', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
                        <div style={tabDivStyle}>
                            <Tabs>
                                <TabList className={"main-tab"}>
                                    <Tab>Peripherals</Tab>
                                </TabList>
                                <TabPanel>
                                    <FilterablePeripheralTable  peripherals={this.state.peripherals}
                                                                selectedBoard={this.state.selectedBoard}
                                                                QSPIrequired={this.state.QSPIrequired}
                                                                TMCActive={this.state.TMCActive || is_x2xx_stacked || is_x2xx_csp}
                                                                TMCDisabled={is_x2xx_stacked || is_x2xx_csp}
                                                                TMCCallback={this.handleTMCChange}
                                                                clickListCallback={this.clickListCallback}/>
                                </TabPanel>
                            </Tabs>
                        </div>
                    </div>
                    <div style={{ flex: 11, height: 'calc(100vh - 80px)', overflow: 'auto' }}>
                        <div style={tabDivStyle}>
                            <Tabs>
                                <TabList className={"main-tab"}>
                                    <Tab>Pin Array</Tab>
                                    <Tab>Pin Layout</Tab>
                                    <Button onClick={this.toggleLegendModal} color="success" style={modalButtonStyle}>Legend</Button>
                                </TabList>
                                <Modal isOpen={this.state.showLegend} toggle={this.toggleLegendModal} className={'modal-dialog-centered'}>
                                    <ModalHeader toggle={this.toggleLegendModal}>Legend:</ModalHeader>
                                    <ModalBody>
                                        <div id="legend-wrapper">
                                            <div style={modalListStyle} className='legend'>
                                                <span style={{marginLeft:"12px", fontWeight:"bold"}}>Pin Array:</span>
                                                <ul>
                                                    <li/>
                                                    <li>
                                                        <div className='matrix-block pin-layout-fixed'/>Fixed Pin
                                                    </li>
                                                    <li>
                                                        <div className='matrix-block pin-layout-unselected'/>Unused Pin
                                                    </li>
                                                    <li>
                                                        <div className='matrix-block pin-layout-selected'/>Selected Pin
                                                    </li>
                                                </ul>
                                            </div>
                                            <div style={modalListStyle} className='legend'>
                                                <span style={{marginLeft:"12px", fontWeight:"bold"}}>Pin Layout:</span>
                                                <br/>
                                                <ul>
                                                    <li/>
                                                    <li>
                                                        <div className='matrix-block disabled'/>Disabled
                                                    </li>
                                                    <li>
                                                        <div className='matrix-block unavailable'/>Unavailable
                                                    </li>
                                                    <li>
                                                        <div className='matrix-block available'/>Available
                                                    </li>
                                                    <li>
                                                        <div className='matrix-block used-pin'/>Used
                                                    </li>
                                                    <li>
                                                        <div className='matrix-block selected'/>Selected
                                                    </li>
                                                    <li>
                                                        <div className='matrix-block selected-fixed'/>Fixed Selected
                                                    </li>
                                                    <li>
                                                        <div className='matrix-block selected-warning'/>Warning
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </ModalBody>
                                </Modal>
                                <TabPanel style={{position:'relative', paddingLeft:"2px"}}>
                                    <PeripheralPinArray peripherals={this.state.peripherals}
                                                        selectedBoard={this.state.selectedBoard}
                                                        TMCActive={this.state.TMCActive}
                                                        force_TMC={is_x2xx_stacked || is_x2xx_csp}
                                                        pinMap={this.state.pinMap}
                                                        pullUps={this.state.pullUps}
                                                        pinStatus={this.state.pinStatus}
                                                        clickedPinCallback={this.clickPinCallback}
                                                        clickedPullupCallback={this.clickedPullupCallback}/>
                                </TabPanel>
                                <TabPanel>
                                    <SvgComponent
                                        id="pinLayout"
                                        ref={el => (this.componentRef = el)}
                                        key={"svgcomponent-"+this.state.TMCActive+"-"+QSPI.selected}
                                        board={this.state.selectedBoard}
                                        TMCActive={this.state.TMCActive}
                                        pinMap={this.state.pinMap}
                                        listHeight={this.state.listHeight}
                                        peripherals={this.state.peripherals}
                                        activeList={activeList}
                                        updateAnnotate={this.state.updateAnnotate}
                                        pinStatus={this.state.pinStatus}/>
                                </TabPanel>
                            </Tabs>
                        </div>
                    </div>
                    {/*<div style={{ flex: 3 }}>
                        <div style={tabDivStyle}>
                            <Tabs>
                                <TabList className={"main-tab"}>
                                    <Tab>Pin Names</Tab>
                                </TabList>
                                <TabPanel>
                                    <PinDetailList peripherals={this.state.peripherals} />
                                </TabPanel>
                            </Tabs>
                        </div>
                    </div>*/}
                </div>
            </div>
        );
    }
}

export default App;
